import { Component, DestroyRef, ElementRef, NgZone, ViewChild, afterNextRender } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SessionService } from '../core/auth/session.service';
import { LoginService } from '../core/api/login.api';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  @ViewChild('appHeader') private appHeader?: ElementRef<HTMLElement>;
  @ViewChild('viewWrap') private viewWrap?: ElementRef<HTMLElement>;

  constructor(
    public session: SessionService,
    private loginService: LoginService,
    private router: Router,
    private zone: NgZone,
    destroyRef: DestroyRef
  ) {
    // The content area scrolls instead of the window, so it keeps its offset across routes.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.viewWrap) {
          this.viewWrap.nativeElement.scrollTop = 0;
        }
      });

    afterNextRender(() => {
      const header = this.appHeader?.nativeElement;
      if (!header) {
        return;
      }

      const publishHeight = () => {
        const height = Math.round(header.getBoundingClientRect().height);
        if (height === 0) {
          return;
        }
        document.documentElement.style.setProperty('--app-header-height', `${height}px`);
      };

      // iPad Safari ResizeObserver can re-enter forever when header height
      // writes a CSS variable that changes body padding. Measure once.
      this.zone.runOutsideAngular(() => {
        publishHeight();
        const isiOS =
          /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (isiOS) {
          return;
        }
        let lastHeight = -1;
        const observer = new ResizeObserver(() => {
          const height = Math.round(header.getBoundingClientRect().height);
          if (height === 0 || Math.abs(height - lastHeight) <= 1) {
            return;
          }
          lastHeight = height;
          publishHeight();
        });
        observer.observe(header);
        destroyRef.onDestroy(() => observer.disconnect());
      });
    });
  }

  toggleSidebar(): void {
    document.getElementById('wrapper')?.classList.toggle('toggled');
  }

  closeSidebarIfOpen(): void {
    const wrapper = document.getElementById('wrapper');
    if (wrapper && getComputedStyle(wrapper).paddingLeft !== '0px') {
      wrapper.classList.toggle('toggled');
    }
  }

  get loginName(): string {
    return this.session.loginName() || this.session.superName() || this.session.emailAddress();
  }

  isAM(): boolean {
    return (
      parseInt(this.session.amkey() || '0', 10) > 0 ||
      this.session.builderAccess() === 'true' ||
      parseInt(this.session.skey() || '0', 10) > 0
    );
  }

  showMainMenu(): boolean {
    const portal = parseInt(this.session.portalId() || '0', 10);
    return portal === 1 || portal === 2 || portal === 5;
  }

  isEngineering(): boolean {
    return parseInt(this.session.portalId() || '0', 10) === 4;
  }

  isIPPayInspectorUser(): boolean {
    return this.session.isIPPayInspector() === 'true';
  }

  toMainMenu(): void {
    const portal = parseInt(this.session.portalId() || '0', 10);
    if (portal === 1) {
      this.router.navigateByUrl(`/dashboard/${this.session.emailLoginId()}`);
    } else if (portal === 2) {
      this.router.navigateByUrl(`/inspectordashboard/${this.session.emailLoginId()}`);
    } else if (portal === 3) {
      this.router.navigateByUrl('/ptportal');
    } else if (portal === 5) {
      this.router.navigateByUrl('/hvacDashboard');
    }
    this.toggleSidebar();
  }

  toIPPaymentHistory(): void {
    if (this.isIPPayInspectorUser()) {
      this.router.navigateByUrl('/inspectionsPayHistory');
    }
    this.toggleSidebar();
  }

  toAddressPlans(): void {
    if (this.isEngineering()) {
      this.router.navigateByUrl('/engineeringAddressPlans');
    }
    this.toggleSidebar();
  }

  toDpisPlanVault(): void {
    if (this.isEngineering()) {
      this.router.navigateByUrl('/engineeringPlanVault');
    }
    this.toggleSidebar();
  }

  login(): void {
    this.router.navigateByUrl('/');
    this.toggleSidebar();
  }

  logout(): void {
    sessionStorage.clear();
    this.session.clearSessionCookies();
    this.loginService.SignOut().subscribe({ error: () => undefined });
    this.router.navigateByUrl('/');
    this.toggleSidebar();
  }

  scrollTop(): void {
    this.viewWrap?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
